"use client";

import { trackAgentRevenueOfferShown } from "@/lib/analytics";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";

/**
 * OfferImpression — a render-nothing client island that fires the `offer_seen`
 * (`agent_revenue_offer_shown`) impression EXACTLY ONCE per offer presentation.
 * Part of the offer-placement architecture (ADR-0025).
 *
 * Why this exists: server-rendered offer surfaces (e.g. CalculatorReportBridge,
 * used by muhurat / dasha / sade-sati) can't use React hooks, so they emitted NO
 * `offer_seen` event and read ~0% reach in the funnel even though a priced report
 * CTA was on screen. Mount this island beside such a CTA to make the surface's
 * reach measurable — without converting the whole server surface to a client
 * component. Re-fire-proof by construction via {@link useImpressionOnce} (gated on
 * a stable string key, never object identity — the 2026-06 re-fire incident class).
 */
export default function OfferImpression({
  planId,
  sourcePath,
  intent,
  placement,
  customerProblem,
  urgency = "medium",
  routeRankScore,
  routeRankReason,
}: {
  planId: string;
  sourcePath: string;
  intent: string;
  placement: string;
  customerProblem?: string;
  urgency?: string;
  routeRankScore?: number;
  routeRankReason?: string;
}) {
  useImpressionOnce(`${planId}|${placement}|${sourcePath}`, () => {
    trackAgentRevenueOfferShown({
      offer_type: "report",
      plan_id: planId,
      source_path: sourcePath,
      intent,
      placement,
      checkout_channel: "ishvaram_report",
      commerce_system: "ishvaram",
      customer_problem: customerProblem,
      urgency,
      route_rank_score: routeRankScore,
      route_rank_reason: routeRankReason,
      ranked_offer_order: [planId],
    });
  });
  return null;
}
